/**
 * "Run now" – fires a scheduled automation off-schedule from the record
 * slideout footer. Renders only for `ahoy_scheduled_automation` records;
 * `null` everywhere else so it's inert when composed into shared surfaces.
 */

import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { ApiError } from '@/lib/errors';
import type { CrmObject } from '@/types/crm';

import { SCHEDULED_AUTOMATION_ENTITY_TYPE, triggerRunNow } from './runNow';

export function RunNowButton({ object }: { object: CrmObject }) {
  const [isPending, setIsPending] = useState(false);

  const isScheduled =
    object.entity_type_name === SCHEDULED_AUTOMATION_ENTITY_TYPE;

  const handleRun = useCallback(async () => {
    setIsPending(true);
    try {
      await triggerRunNow(object.org_id, object.entity_id);
      toast.success('Automation run started – see the Runs tab for the result');
    } catch (error) {
      console.error(error);
      if (error instanceof ApiError && error.is(429)) {
        toast.error(
          'Too many requests right now. Wait a moment and try again.'
        );
      } else {
        toast.error(
          "We couldn't run this automation. Try again, and get in touch if it keeps happening."
        );
      }
    } finally {
      setIsPending(false);
    }
  }, [object.org_id, object.entity_id]);

  if (!isScheduled) {
    return null;
  }

  return (
    <Button
      size='sm'
      color='secondary'
      onClick={handleRun}
      isDisabled={isPending}
    >
      Run now
    </Button>
  );
}
